import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { FlightMap } from '../components/map/FlightMap';
import WeatherPanel from '../components/weather/WeatherPanel';
import { polygonCentroid } from '../components/weather/polygonCentroid';
import { dronesApi, missionsApi } from '../lib/api';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check,
  Plane,
  Map,
  Settings,
  CalendarIcon,
  Clock,
  AlertTriangle,
  Building2,
  Users,
  Eye,
  Radio,
  GraduationCap,
  Gauge,
  UserCheck,
  Radar,
  Package,
  Moon,
  Shield,
  Factory,
  Zap,
  Maximize2,
  Minimize2
} from 'lucide-react';

const MISSION_TYPES = [
  { value: 'inspection', label: 'Inspection technique' },
  { value: 'cartographie', label: 'Cartographie / Photogrammétrie' },
  { value: 'surveillance', label: 'Surveillance' },
  { value: 'agriculture', label: 'Agriculture de précision' },
  { value: 'audiovisuel', label: 'Prise de vue audiovisuelle' },
  { value: 'livraison', label: 'Livraison' },
  { value: 'autre', label: 'Autre' },
];

const STEPS = [
  { id: 1, title: 'Drone', icon: Plane },
  { id: 2, title: 'Zone de vol', icon: Map },
  { id: 3, title: 'Paramètres', icon: Settings },
  { id: 4, title: 'Confirmation', icon: Check },
];

export default function NewMissionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [wideMode, setWideMode] = useState(false);
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    drone_id: '',
    mission_type: '',
    polygon_geometry: null,
    altitude_requested: 100,
    mission_date: new Date(),
    mission_time: '10:00',
    is_night_mission: false,
    is_urban_zone: false,
    // Champs EU 2019/947
    flight_type: 'vlos',
    min_distance_persons: 150,
    low_speed_mode: false,
    pilot_certification: 'a1a3',
    remote_id_active: true,
    crowd_overfly: false,
    // Champs SORA
    bvlos_distance_km: 0,
    payload_type: 'standard',
    night_flight_duration_min: 0,
    critical_infrastructure: false,
    infrastructure_types: []
  });

  const [selectedDrone, setSelectedDrone] = useState(null);

  // Scroll vers le haut quand on change d'étape
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    loadDrones();
    // Product analytics: entering the New Mission wizard is the strongest
    // signal of intent between "map exploration" and actual conversion.
    trackEvent('mission_started');

    // If a visitor drew a polygon on the public landing map before
    // signing up, restore it here so they don't have to redraw. Cleared
    // after consumption so it doesn't leak into future mission creations.
    try {
      const stashed = sessionStorage.getItem('dronemaps_pending_mission_polygon');
      if (stashed) {
        const parsed = JSON.parse(stashed);
        if (parsed?.geometry) {
          setFormData((prev) => ({ ...prev, polygon_geometry: parsed.geometry }));
          toast.info('Zone dessinée depuis l\'accueil restaurée — étape 2 déjà pré-remplie 👇');
          trackEvent('cta_clicked', { name: 'landing_polygon_restored' });
        }
        sessionStorage.removeItem('dronemaps_pending_mission_polygon');
      }
    } catch { /* private mode / malformed JSON — ignore */ }
  }, []);

  const loadDrones = async () => {
    try {
      const response = await dronesApi.getAll();
      setDrones(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des drones');
    } finally {
      setLoading(false);
    }
  };

  const handleDroneSelect = (droneId) => {
    const drone = drones.find(d => d.id === droneId);
    setSelectedDrone(drone);
    setFormData({ ...formData, drone_id: droneId });
  };

  const handlePolygonDrawn = (geometry) => {
    setFormData({ ...formData, polygon_geometry: geometry });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.drone_id !== '';
      case 2:
        return formData.polygon_geometry !== null;
      case 3:
        return formData.mission_type !== '' && formData.altitude_requested > 0;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (user?.subscription_status === 'free' && user?.missions_count >= 3) {
      toast.error('Limite de missions gratuites atteinte. Passez au plan Solo.');
      navigate('/subscription');
      return;
    }

    setSubmitting(true);

    try {
      const response = await missionsApi.create({
        ...formData,
        mission_date: format(formData.mission_date, 'yyyy-MM-dd'),
      });
      toast.success('Mission créée avec succès !');
      trackEvent('mission_saved', {
        risk_level: response?.data?.risk_level || null,
      });
      navigate(`/missions/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Nouvelle Mission">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (drones.length === 0) {
    return (
      <DashboardLayout title="Nouvelle Mission">
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center max-w-lg mx-auto">
          <Plane className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2 font-[Barlow]">
            Aucun drone enregistré
          </h3>
          <p className="text-slate-600 mb-6">
            Vous devez d'abord ajouter un drone pour créer une mission.
          </p>
          <Button
            onClick={() => navigate('/drones')}
            className="bg-sky-500 hover:bg-sky-600 text-white"
          >
            Ajouter un drone
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Nouvelle Mission">
      {/* Wide Mode Overlay (full-screen map editing, sidebar hidden) */}
      {currentStep === 2 && wideMode && (
        <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="wide-mode-overlay">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-3">
              <Map className="w-5 h-5 text-sky-600" />
              <span className="font-semibold text-slate-900 font-[Barlow]">Définissez la zone de vol</span>
              {formData.polygon_geometry && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded border border-emerald-200">
                  <Check className="w-3.5 h-3.5" /> Zone définie
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setWideMode(false)}
                className="text-sm"
                data-testid="exit-wide-mode"
              >
                <Minimize2 className="w-4 h-4 mr-1" />
                Quitter le mode large
              </Button>
              <Button
                onClick={() => { setWideMode(false); setCurrentStep(3); }}
                disabled={!formData.polygon_geometry}
                className="bg-sky-500 hover:bg-sky-600 text-white text-sm"
                data-testid="wide-mode-next"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          <div className="flex-1">
            <FlightMap
              onPolygonDrawn={handlePolygonDrawn}
              initialPolygon={formData.polygon_geometry}
              showZones={true}
              editable={true}
              height="100%"
              userCountry={user?.country}
            />
          </div>
        </div>
      )}

      {/* Stepper (hidden in step 2 to maximise map area) */}
      {currentStep !== 2 && (
      <div className="mb-8">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            return (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                    transition-colors duration-200
                    ${isCompleted ? 'bg-emerald-500 text-white' : 
                      isActive ? 'bg-sky-500 text-white' : 
                      'bg-slate-200 text-slate-500'}
                  `}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${isActive ? 'text-sky-600' : 'text-slate-500'}`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`
                    w-16 sm:w-24 h-0.5 mx-2 mt-[-20px]
                    ${currentStep > step.id ? 'bg-emerald-500' : 'bg-slate-200'}
                  `} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Step Content */}
      <div className={currentStep === 2 ? 'w-full' : 'max-w-4xl mx-auto'}>
        {/* Step 1: Select Drone */}
        {currentStep === 1 && (
          <div className="animate-fade-in">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 font-[Barlow]">
              Sélectionnez votre drone
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drones.map((drone) => (
                <button
                  key={drone.id}
                  onClick={() => handleDroneSelect(drone.id)}
                  className={`
                    p-4 rounded-lg border-2 text-left transition-all duration-200
                    ${formData.drone_id === drone.id 
                      ? 'border-sky-500 bg-sky-50' 
                      : 'border-slate-200 hover:border-slate-300 bg-white'}
                  `}
                  data-testid={`select-drone-${drone.id}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Plane className={`w-5 h-5 ${formData.drone_id === drone.id ? 'text-sky-600' : 'text-slate-400'}`} />
                    <span className="font-semibold text-slate-900">{drone.model_name}</span>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p>Classe {drone.ce_class} • {drone.weight_kg} kg</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Draw Zone */}
        {currentStep === 2 && !wideMode && (
          <div className="animate-fade-in">
            <div className="flex items-start justify-between mb-2 gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1 font-[Barlow]">
                  Définissez la zone de vol
                </h2>
                <p className="text-slate-600 text-sm">
                  Utilisez les outils de dessin pour tracer un polygone ou un rectangle sur la carte.
                </p>
              </div>
              <button
                onClick={() => setWideMode(true)}
                className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors"
                data-testid="enter-wide-mode"
                title="Carte en mode large (cache la sidebar)"
              >
                <Maximize2 className="w-4 h-4" />
                Mode large
              </button>
            </div>
            <div className="map-container" style={{ height: 'calc(100vh - 160px)', minHeight: '600px' }}>
              <FlightMap
                onPolygonDrawn={handlePolygonDrawn}
                initialPolygon={formData.polygon_geometry}
                showZones={true}
                editable={true}
                height="100%"
                userCountry={user?.country}
              />
            </div>
            {formData.polygon_geometry && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-700">
                <Check className="w-5 h-5" />
                <span>Zone de vol définie</span>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Mission Parameters */}
        {currentStep === 3 && (
          <div className="animate-fade-in">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 font-[Barlow]">
              Paramètres de la mission
            </h2>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">
              <div>
                <Label htmlFor="mission_type">Type de mission *</Label>
                <Select
                  value={formData.mission_type}
                  onValueChange={(value) => setFormData({ ...formData, mission_type: value })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="mission-type-select">
                    <SelectValue placeholder="Sélectionnez le type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MISSION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="altitude">Altitude demandée (m) *</Label>
                  <Input
                    id="altitude"
                    type="number"
                    min="0"
                    max="500"
                    value={formData.altitude_requested}
                    onChange={(e) => setFormData({ ...formData, altitude_requested: parseInt(e.target.value) || 0 })}
                    className="mt-1.5"
                    data-testid="altitude-input"
                  />
                  {formData.altitude_requested > 120 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Au-delà de 120m, une autorisation est requise
                    </p>
                  )}
                </div>

                <div>
                  <Label>Heure de vol</Label>
                  <Input
                    type="time"
                    value={formData.mission_time}
                    onChange={(e) => setFormData({ ...formData, mission_time: e.target.value })}
                    className="mt-1.5"
                    data-testid="mission-time-input"
                  />
                </div>
              </div>

              <div>
                <Label>Date de la mission</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full mt-1.5 justify-start text-left font-normal"
                      data-testid="mission-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.mission_date 
                        ? format(formData.mission_date, 'PPP', { locale: fr })
                        : 'Sélectionnez une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.mission_date}
                      onSelect={(date) => setFormData({ ...formData, mission_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="night"
                  checked={formData.is_night_mission}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_night_mission: checked })}
                  data-testid="night-mission-checkbox"
                />
                <Label htmlFor="night" className="cursor-pointer">
                  Mission de nuit
                </Label>
              </div>

              {/* Zone urbaine - CRITIQUE pour la réglementation */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="urban"
                    checked={formData.is_urban_zone}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_urban_zone: checked })}
                    className="mt-0.5"
                    data-testid="urban-zone-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="urban" className="cursor-pointer flex items-center gap-2 text-amber-800 font-semibold">
                      <Building2 className="w-4 h-4" />
                      Vol en zone peuplée / agglomération
                    </Label>
                    <p className="text-xs text-amber-700 mt-1">
                      Cochez cette case si votre zone de vol se situe en agglomération, ville, ou à proximité 
                      de personnes. Consultez la couche "IGN Officiel" sur la carte pour vérifier.
                    </p>
                    {formData.is_urban_zone && selectedDrone && (
                      <div className="mt-2 p-2 bg-amber-100 rounded text-xs text-amber-800">
                        <strong>⚠️ Attention :</strong>
                        {selectedDrone.weight_kg > 4 && (
                          <span> Drone de {selectedDrone.weight_kg}kg → Catégorie SPECIFIC obligatoire.</span>
                        )}
                        {selectedDrone.ce_class === 'C3' || selectedDrone.ce_class === 'C4' ? (
                          <span> Classe {selectedDrone.ce_class} non autorisée en zone peuplée.</span>
                        ) : null}
                        {selectedDrone.weight_kg <= 4 && !['C3', 'C4'].includes(selectedDrone.ce_class) && (
                          <span> Autorisation préfectorale probablement requise.</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* NOUVEAUX PARAMÈTRES EU 2019/947 */}
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-sky-500" />
                  Paramètres avancés (EU 2019/947)
                </h3>

                <div className="space-y-4">
                  {/* Type de vol VLOS/BVLOS */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <Label className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                      <Eye className="w-4 h-4" />
                      Type de vol
                    </Label>
                    <Select
                      value={formData.flight_type}
                      onValueChange={(value) => setFormData({ ...formData, flight_type: value })}
                    >
                      <SelectTrigger data-testid="flight-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vlos">VLOS - Vol à vue (obligatoire en Open)</SelectItem>
                        <SelectItem value="bvlos">BVLOS - Hors vue (STS-02 / C6 uniquement)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.flight_type === 'bvlos' && selectedDrone?.ce_class !== 'C6' && (
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ BVLOS limité autorisé uniquement avec drone classe C6 (STS-02). Sinon autorisation SORA requise.
                      </p>
                    )}
                  </div>

                  {/* Formation pilote */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <Label className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                      <GraduationCap className="w-4 h-4" />
                      Formation pilote
                    </Label>
                    <Select
                      value={formData.pilot_certification}
                      onValueChange={(value) => setFormData({ ...formData, pilot_certification: value })}
                    >
                      <SelectTrigger data-testid="pilot-cert-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="a1a3">A1/A3 - Attestation en ligne (Open)</SelectItem>
                        <SelectItem value="a2">A2 - Formation + examen pratique</SelectItem>
                        <SelectItem value="sts01">STS-01 - Formation scénario standard</SelectItem>
                        <SelectItem value="sts02">STS-02 - Formation BVLOS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Mode basse vitesse - uniquement pour C2 */}
                  {selectedDrone?.ce_class === 'C2' && (
                    <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="lowspeed"
                          checked={formData.low_speed_mode}
                          onCheckedChange={(checked) => setFormData({ ...formData, low_speed_mode: checked })}
                          className="mt-0.5"
                          data-testid="low-speed-checkbox"
                        />
                        <div className="flex-1">
                          <Label htmlFor="lowspeed" className="cursor-pointer flex items-center gap-2 text-sky-800 font-semibold">
                            <Gauge className="w-4 h-4" />
                            Mode basse vitesse (C2)
                          </Label>
                          <p className="text-xs text-sky-700 mt-1">
                            En mode basse vitesse (max 3 m/s), la distance minimale passe de <strong>30m à 5m</strong> en A2.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Remote ID */}
                  <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="remoteid"
                      checked={formData.remote_id_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, remote_id_active: checked })}
                      data-testid="remote-id-checkbox"
                    />
                    <Label htmlFor="remoteid" className="cursor-pointer flex items-center gap-2">
                      <Radio className="w-4 h-4 text-emerald-500" />
                      Remote ID actif
                    </Label>
                    {selectedDrone && !['C0', 'legacy'].includes(selectedDrone.ce_class) && !formData.remote_id_active && (
                      <span className="text-xs text-red-500 ml-2">⚠️ Obligatoire pour {selectedDrone.ce_class}</span>
                    )}
                  </div>

                  {/* Survol de foule */}
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="crowd"
                        checked={formData.crowd_overfly}
                        onCheckedChange={(checked) => setFormData({ ...formData, crowd_overfly: checked })}
                        className="mt-0.5"
                        data-testid="crowd-overfly-checkbox"
                      />
                      <div className="flex-1">
                        <Label htmlFor="crowd" className="cursor-pointer flex items-center gap-2 text-red-800 font-semibold">
                          <Users className="w-4 h-4" />
                          Survol de rassemblement / foule prévu
                        </Label>
                        <p className="text-xs text-red-700 mt-1">
                          Le survol de foule est <strong>INTERDIT</strong> en catégorie Open.
                        </p>
                        {formData.crowd_overfly && (
                          <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-800">
                            <strong>⛔ ATTENTION :</strong> Autorisation Certified ou SORA requise.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION SORA - Paramètres déclenchant SORA */}
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  Paramètres SORA (opérations spéciales)
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Ces paramètres peuvent déclencher une analyse SORA (45-90 jours DSAC) si les scénarios STS ne sont pas applicables.
                </p>

                <div className="space-y-4">
                  {/* Distance BVLOS */}
                  {formData.flight_type === 'bvlos' && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <Label className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                        <Radar className="w-4 h-4" />
                        Distance BVLOS prévue
                      </Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="0.5"
                          value={formData.bvlos_distance_km}
                          onChange={(e) => setFormData({ ...formData, bvlos_distance_km: parseFloat(e.target.value) })}
                          className="flex-1"
                          data-testid="bvlos-distance-slider"
                        />
                        <span className="font-mono text-lg font-semibold w-20 text-right">
                          {formData.bvlos_distance_km} km
                        </span>
                      </div>
                      {formData.bvlos_distance_km > 2 && (
                        <p className="text-xs text-amber-600 mt-2">
                          ⚠️ BVLOS &gt; 2km dépasse STS-02 → SORA obligatoire
                        </p>
                      )}
                      {formData.bvlos_distance_km > 0 && formData.bvlos_distance_km <= 2 && selectedDrone?.ce_class === 'C6' && (
                        <p className="text-xs text-emerald-600 mt-2">
                          ✓ STS-02 applicable (C6 + BVLOS ≤ 2km en zone faible densité)
                        </p>
                      )}
                    </div>
                  )}

                  {/* Type de payload */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <Label className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                      <Package className="w-4 h-4" />
                      Type de payload / mission spéciale
                    </Label>
                    <Select
                      value={formData.payload_type}
                      onValueChange={(value) => setFormData({ ...formData, payload_type: value })}
                    >
                      <SelectTrigger data-testid="payload-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">📷 Standard (caméra, capteur)</SelectItem>
                        <SelectItem value="epandage">🌾 Épandage phytosanitaire</SelectItem>
                        <SelectItem value="largage">📦 Largage d'objets</SelectItem>
                        <SelectItem value="thermographie">🔥 Thermographie IR sensible</SelectItem>
                        <SelectItem value="autre_sensible">⚙️ Autre payload sensible</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.payload_type !== 'standard' && (
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ Payload spécial → SORA probable (analyse de risque requise)
                      </p>
                    )}
                  </div>

                  {/* Vol de nuit prolongé */}
                  {formData.is_night_mission && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <Label className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                        <Moon className="w-4 h-4" />
                        Durée prévue du vol de nuit
                      </Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="number"
                          min="0"
                          max="180"
                          value={formData.night_flight_duration_min}
                          onChange={(e) => setFormData({ ...formData, night_flight_duration_min: parseInt(e.target.value) || 0 })}
                          className="w-24 px-3 py-2 border rounded-md"
                          data-testid="night-duration-input"
                        />
                        <span className="text-sm text-slate-600">minutes</span>
                      </div>
                      {formData.night_flight_duration_min > 30 && formData.is_urban_zone && (
                        <p className="text-xs text-amber-600 mt-2">
                          ⚠️ Vol de nuit &gt; 30min en zone urbaine → SORA probable
                        </p>
                      )}
                    </div>
                  )}

                  {/* Infrastructures critiques */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="critical_infra"
                        checked={formData.critical_infrastructure}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          critical_infrastructure: checked,
                          infrastructure_types: checked ? formData.infrastructure_types : []
                        })}
                        className="mt-0.5"
                        data-testid="critical-infra-checkbox"
                      />
                      <div className="flex-1">
                        <Label htmlFor="critical_infra" className="cursor-pointer flex items-center gap-2 text-amber-800 font-semibold">
                          <Factory className="w-4 h-4" />
                          Survol d'infrastructures critiques
                        </Label>
                        <p className="text-xs text-amber-700 mt-1">
                          Aéroport, prison, centrale électrique/nucléaire, zone militaire, hôpital...
                        </p>
                      </div>
                    </div>

                    {formData.critical_infrastructure && (
                      <div className="mt-4 ml-8 space-y-2">
                        <p className="text-xs font-semibold text-amber-800 mb-2">Types d'infrastructures concernées :</p>
                        {[
                          { id: 'aeroport', label: '✈️ Aéroport / Héliport', icon: Plane },
                          { id: 'prison', label: '🔒 Établissement pénitentiaire', icon: Shield },
                          { id: 'centrale', label: '⚡ Centrale électrique / nucléaire', icon: Zap },
                          { id: 'militaire', label: '🎖️ Zone militaire', icon: Shield },
                          { id: 'hopital', label: '🏥 Hôpital / Centre de soins', icon: Building2 }
                        ].map(infra => (
                          <div key={infra.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`infra_${infra.id}`}
                              checked={formData.infrastructure_types.includes(infra.id)}
                              onCheckedChange={(checked) => {
                                const newTypes = checked 
                                  ? [...formData.infrastructure_types, infra.id]
                                  : formData.infrastructure_types.filter(t => t !== infra.id);
                                setFormData({ ...formData, infrastructure_types: newTypes });
                              }}
                            />
                            <Label htmlFor={`infra_${infra.id}`} className="text-sm text-amber-800 cursor-pointer">
                              {infra.label}
                            </Label>
                          </div>
                        ))}
                        <p className="text-xs text-red-600 mt-3">
                          ⛔ Le survol d'infrastructures critiques nécessite généralement une autorisation SORA
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 4 && (
          <div className="animate-fade-in">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 font-[Barlow]">
              Récapitulatif de la mission
            </h2>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500">Drone</p>
                  <p className="font-semibold text-slate-900">{selectedDrone?.model_name}</p>
                  <p className="text-sm text-slate-600">Classe {selectedDrone?.ce_class} • {selectedDrone?.weight_kg} kg</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Type de mission</p>
                  <p className="font-semibold text-slate-900">
                    {MISSION_TYPES.find(t => t.value === formData.mission_type)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date et heure</p>
                  <p className="font-semibold text-slate-900">
                    {format(formData.mission_date, 'PPP', { locale: fr })} à {formData.mission_time}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Altitude demandée</p>
                  <p className="font-semibold text-slate-900 font-mono">{formData.altitude_requested} m</p>
                </div>
              </div>

              {formData.is_night_mission && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span>Mission de nuit - conditions supplémentaires applicables</span>
                </div>
              )}

              {formData.is_urban_zone && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <Building2 className="w-5 h-5" />
                  <span>
                    <strong>Zone peuplée</strong> - Autorisation préfectorale probablement requise
                    {selectedDrone?.weight_kg > 4 && ' • Drone >4kg = Catégorie SPECIFIC'}
                  </span>
                </div>
              )}

              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-2">Zone de vol</p>
                <div className="h-72 rounded-lg overflow-hidden border border-slate-200">
                  <FlightMap
                    initialPolygon={formData.polygon_geometry}
                    showZones={true}
                    editable={false}
                    compactMode={true}
                    height="100%"
                    userCountry={user?.country}
                  />
                </div>
              </div>

              {/* Weather briefing — captured live and persisted to the mission */}
              {(() => {
                const c = polygonCentroid(formData.polygon_geometry);
                if (!c) return null;
                return (
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-500 mb-2">Briefing météo</p>
                    <WeatherPanel
                      lat={c.lat}
                      lon={c.lon}
                      date={format(formData.mission_date, 'yyyy-MM-dd')}
                      time={formData.mission_time}
                      ceClass={selectedDrone?.ce_class}
                      droneName={selectedDrone?.model_name}
                    />
                  </div>
                );
              })()}
            </div>

            <div className="mt-6 p-4 bg-slate-100 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-600">
                <strong>Disclaimer:</strong> Drone Maps fournit une aide à la vérification réglementaire mais ne se substitue pas 
                aux autorités. L'utilisateur reste seul responsable de la conformité de ses vols.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={() => currentStep === 1 ? navigate('/missions') : setCurrentStep(currentStep - 1)}
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {currentStep === 1 ? 'Annuler' : 'Précédent'}
          </Button>

          {currentStep < 4 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="bg-sky-500 hover:bg-sky-600 text-white"
              data-testid="next-step-btn"
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-mission-btn"
            >
              {submitting ? 'Analyse en cours...' : 'Lancer l\'analyse'}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
